import type { MediumData } from '@expert/types';
import type { InjectionKey } from "vue";

export const PAYBACK_PREFIX: string = '308342';

export interface StoreReference {
	currentStore: Ref<Store | null>;
	updateFunction: (newId: string) => void|Promise<void>;
}

export const EventContextKeys = {
    NEO_SESSION: 'neoSession',
    SHOP_SESSION: 'shopSession',
} as const;

export const CookieNames = {
    NEO_SESSION_COOKIE: 'session',
    NEO_SESS_COOKIE: '_sess',
    STORE_COOKIE: 'fmarktcookie',
    AB_CONFIG_COOKIE: 'expertShopABConfig',
} as const;

export const StateNames = {
    NEO_SESSION: 'session',
    APP_USER_SESSION: 'appUserSession',
    APP_USER_PROFILE: 'appUserProfile',
    USER_FEATURE_STATES: 'userFeatureStates',
} as const;

export class ProvideNames {
    public static readonly ENABLE_PROXY_DEBUG_LOGGING: boolean = false;
    public static readonly ENABLE_PROXY_TIME_LOGGING: boolean = false;

    public static readonly BASE_DIR: string = 'baseDir';
    public static readonly PLACEHOLDER_IMAGE: string = '/17/89/06/2d7e8a948fb264651203723a2101d1283b/defaultImage_100x100_90-0.jpeg';

    public static readonly CURRENT_STORE_KEY: InjectionKey<StoreReference> = Symbol("currentStore");

    public static readonly DEFAULT_CENTRAL_STORE: Store = {
		id: 'e_2879130',
		name: 'expert e-Commerce',
        email: 'kundenservice@expert-commerce.de',
        street: "Bayernstraße 4",
        zip: '30855',
        city: 'Langenhagen',
        phone: '0203 80481842',
        majorCustomerPhone: '',
        represantiveName: 'expert e-Commerce GmbH',
        companyGroupId: '0',
        cartSwapOnlineReservation: false,
        storeId: '2879130',
        storeLink: '',
        storeLogo: '',
        position: {
            lat: 52.4435567,
            lng: 9.7101468,
        },
		storeLogoImg: '47/f1/37/344581753cb59b3dfbe24d781b76f70899/expert_logo.svg',
        storeLogoDarkImg: 'a0/d1/90/7ad964598b9b63833e1a4fa34bd0c2aa98/logo_2879130.svg',
        fbPixelId: null,
		videoConsultation: true,
        showAlternativeAvailabilityForStores: [],
        institutionMappings: [
            {
                institution: 'e_0',
                roles: ['CompanyGroup'],
            }
        ],
        openings: null,
        paybackStore: true,
	};

    public static readonly STORE_COOKIE_NAME: string = 'fmarktcookie';
    public static readonly FINDER_SYNONYMS: Array<string> = ["finden", "suchen", "nachbarschaft", "umgebung", "benachbart", "nebenan"];

    public static readonly DEFAULT_MEDIUM: MediumData = {
        id: "default",
        mediumType: "Image",
        originalData: this.PLACEHOLDER_IMAGE,
        name: "default Store Logo",
        alt: "default Store Logo",
        title: "default Store Logo",
        label: "default Store Logo",
    };
}

export class TimeUnit {
    public static readonly MINUTE = 60;
    public static readonly HOUR = 60 * this.MINUTE;
    public static readonly DAY = 24 * this.HOUR;
}

export class CacheTimes {
    public static readonly MINUTE = TimeUnit.MINUTE;
    public static readonly HOUR = TimeUnit.HOUR;
    public static readonly DAY = TimeUnit.DAY;

    public static readonly BRAND_CACHE = CacheTimes.HOUR;
    public static readonly ARTICLE_PDS_CACHE = 2 * CacheTimes.HOUR;
    public static readonly MEDIUM_CACHE = 12 * CacheTimes.HOUR;
    public static readonly CATEGORY_CACHE = 2 * CacheTimes.HOUR;
    public static readonly MANUFACTURERS_CACHE = CacheTimes.HOUR;
    public static readonly FEATURE_FILTER_CACHE = CacheTimes.HOUR;
    public static readonly VARIATIONS_CACHE = 5 * CacheTimes.MINUTE;
    public static readonly ACTIVE_PROMOTIONS_CACHE = 15 * CacheTimes.MINUTE;
    public static readonly STORE_CAMPAIGNS_CACHE = 15 * CacheTimes.MINUTE;
    public static readonly WARRANTY_CACHE = 15 * CacheTimes.MINUTE;
    public static readonly PAYBACK_ENTITLEMENTS_METADATA_CACHE = CacheTimes.DAY;
}

export class Timeouts {
    public static readonly WEBDIS_TIMEOUT = 5 * 1000;
    public static readonly NEO_TIMEOUT = 5 * 1000;
    public static readonly NEA_TIMEOUT = 5 * 1000;
    public static readonly ELASTIC_TIMEOUT = 15 * 1000;
    public static readonly NEO_WARRANTY_TIMEOUT = 1000;
    public static readonly PUB_LIVE_TIMEOUT = 500;
}